<template>
  <v-container fluid>
    <v-card class="mb-4">
      <v-card-title>
        Reporte de Productos de Venta
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.trim="filters.q"
              label="Busqueda general"
              hint="Nombre, codigo, SKU, marca o linea"
              persistent-hint
              clearable
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.trim="filters.reference" label="SKU" clearable outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.trim="filters.code" label="Codigo de barras" clearable outlined dense />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.categoryId"
              :items="categories"
              item-text="NAME"
              item-value="ID"
              label="Categoria"
              clearable
              outlined
              dense
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field v-model.trim="filters.brand" label="Marca" clearable outlined dense />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model.trim="filters.line" label="Linea (modelo)" clearable outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.number="filters.taxRateMin" type="number" min="0" step="0.0001" label="Impuesto min." clearable outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.number="filters.taxRateMax" type="number" min="0" step="0.0001" label="Impuesto max." clearable outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="filters.onlyDollar"
              :items="dollarOptions"
              item-text="text"
              item-value="value"
              label="Precio en DLL"
              outlined
              dense
            />
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center mt-2">
          <v-btn color="primary" class="mr-2 mb-2" :loading="loading" @click="runReport">
            <v-icon left>mdi-play</v-icon>
            Ejecutar
          </v-btn>
          <v-btn text class="mr-2 mb-2" @click="clearFilters">
            <v-icon left>mdi-filter-remove-outline</v-icon>
            Limpiar filtros
          </v-btn>
          <v-btn color="success" class="mb-2" :loading="downloading" :disabled="rows.length === 0" @click="downloadReport">
            <v-icon left>mdi-file-excel</v-icon>
            Descargar Excel
          </v-btn>
          <v-spacer></v-spacer>
          <div class="caption grey--text mb-2">
            {{ meta.totalCount }} registros | Tipo cambio: {{ Number(meta.tipoCambio || 1).toFixed(4) }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>
        Vista previa
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading"
        class="elevation-1"
        dense
        :footer-props="{ itemsPerPageText: 'Filas por pagina', itemsPerPageOptions: [10, 25, 50, 100] }"
      >
        <template #[`item.price`]="{ item }">
          ${{ Number(item.price || 0).toFixed(2) }}
        </template>
        <template #[`item.tax`]="{ item }">
          ${{ Number(item.tax || 0).toFixed(2) }}
        </template>
        <template #[`item.publicPrice`]="{ item }">
          ${{ Number(item.publicPrice || 0).toFixed(2) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import * as productService from '@/services/productService';
import * as categoryService from '@/services/categoryService';

const defaultFilters = () => ({
  q: '',
  reference: '',
  code: '',
  categoryId: null,
  brand: '',
  line: '',
  taxRateMin: null,
  taxRateMax: null,
  onlyDollar: null,
});

export default {
  name: 'InventorySalesProductsReportView',
  data() {
    return {
      loading: false,
      downloading: false,
      categories: [],
      rows: [],
      meta: {
        totalCount: 0,
        tipoCambio: 1,
      },
      filters: defaultFilters(),
      dollarOptions: [
        { text: 'Todos', value: null },
        { text: 'Si', value: true },
        { text: 'No', value: false },
      ],
      headers: [
        { text: 'SKU', value: 'reference' },
        { text: 'Codigo de Barras', value: 'code' },
        { text: 'Descripcion', value: 'name' },
        { text: 'Precio', value: 'price', align: 'end' },
        { text: 'Impuesto', value: 'tax', align: 'end' },
        { text: 'Precio Publico', value: 'publicPrice', align: 'end' },
        { text: 'Categoria', value: 'categoryName' },
        { text: 'Linea', value: 'line' },
        { text: 'Marca', value: 'brand' },
      ],
    };
  },
  async mounted() {
    await this.loadCategories();
    await this.runReport();
  },
  methods: {
    cleanParams() {
      const params = { ...this.filters };
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });
      return params;
    },
    async loadCategories() {
      try {
        this.categories = await categoryService.fetchCategories();
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar categorias.', type: 'error' });
      }
    },
    async runReport() {
      this.loading = true;
      try {
        const response = await productService.fetchSalesReportData(this.cleanParams());
        this.rows = response.rows || [];
        this.meta = response.meta || { totalCount: 0, tipoCambio: 1 };
      } catch (error) {
        this.rows = [];
        this.meta = { totalCount: 0, tipoCambio: 1 };
        this.$emit('show-alert', { message: 'Error al ejecutar el reporte.', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.filters = defaultFilters();
      this.runReport();
    },
    async downloadReport() {
      this.downloading = true;
      try {
        const blob = await productService.downloadSalesReportWithFilters(this.cleanParams());
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `reporte_productos_venta_${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al descargar el reporte.', type: 'error' });
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>
